import { useState, useEffect } from "react";
import {
  Button,
  Input,
  Upload,
  message,
  Row,
  Col,
  Typography,
  Spin,
  Tooltip,
  Modal
} from "antd";
import TextArea from "antd/es/input/TextArea";
import {
  UploadOutlined,
  EditOutlined,
  InfoCircleOutlined,
  FileWordOutlined,
  SaveOutlined,
  DownloadOutlined,
  PictureOutlined,
  DeleteOutlined
} from "@ant-design/icons";
import { getUserID } from "../../Utility/service";
import "./CompanyInfoStyle.scss";
import { axiosSecureInstance } from "../../api/axios";
import { createKnowledgeBase } from "../../api/knowledgeBase";

const { Title, Text } = Typography;
const userId = getUserID();

const CompanyInfo = () => {
  const [companyInfo, setCompanyInfo] = useState({
    name: "",
    website: "",
    description: "",
    files : null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [existingData, setExistingData] = useState(null);
  const [isEditing, setIsEditing] = useState(!existingData);
  const [isGeneratingDoc, setIsGeneratingDoc] = useState(false);
  const [docPreview, setDocPreview] = useState(null);
  const [files, setFiles] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [totalFileList, setTotalFileList] = useState([]);
  const [countTotalFile, setCountTotalFile] = useState(0);
  const [previousFiles, setPreviousFiles] = useState([]);
  const [deletedFileIds, setDeletedFileIds] = useState([]);
  const fetchCompanyInfo = async () => {
    try {
      const response = await axiosSecureInstance.get(`/api/company-submission/${userId}`);
      if (response.status === 200 && response.data) {
        const data = response.data;
        setCompanyInfo({
          name: data.companyName || "",
          website: data.websiteUrl || "",
          description: data.companyInformation || "",
          files : data.fileInfo.filter((file)=>file.generatedDoc !== true) || null,
        });
        setExistingData(data);
        setIsEditing(false);
        const generatedFile = data.fileInfo.filter((file) => file.generatedDoc === true);

        if (generatedFile.length > 0) {
          const fileName = generatedFile[0].fileName.split('/').pop();
          const fileId = generatedFile[0].fileId;
          setDocPreview({
            url: `${process.env.REACT_APP_BASE_URL}api/company-submission/downloads/${fileId}`,
            name: fileName,
          });
        }
      }
    } catch (error) {
      console.log("error : ",error);
      if (error.response?.status === 404) {
        setIsEditing(true);
      } else {
        message.error("Failed to load company information");
      }
    }
  };
  useEffect(() => {
    fetchCompanyInfo();
  }, [userId]);

  const handleChange = (e) => {
    if (isEditing) {
      setCompanyInfo({ ...companyInfo, [e.target.name]: e.target.value });
    }
  };

  const handleEditClick = () => {
    // Initialize fileList with existing files when entering edit mode
    if (companyInfo.files && companyInfo.files.length > 0) {
      const existingFiles = companyInfo.files.map((file, index) => ({
        uid: `existing-${index}`,
        name: file.fileName,
        status: 'done',
        fileId: file.fileId,
        type: file.fileType
      }));
      setFileList(existingFiles);
      setTotalFileList(existingFiles);
      setCountTotalFile(existingFiles.length);
    }
    setIsEditing(true);
  };

  const handleFileChange = (info, field) => {
    if (isEditing) {
      // Update fileList based on the current state of the upload
      setFileList(info.fileList);
      setTotalFileList(info.fileList);
      setCountTotalFile(info.fileList.length);
    }
  };

  const handleRemoveFile = (file) => {    
    if (file.uid.startsWith('existing-')) {
      // For existing files, add to deletedFileIds
      setDeletedFileIds(prev => [...prev, file.fileId]);
    }

    // Update companyInfo state to reflect the removal
    setCompanyInfo(prev => ({
      ...prev,
      files: prev?.files?.filter(f => f.fileId !== file.fileId) || []
    }));
  };

  const handleAddFile = (file) => {
    if (countTotalFile >= 20) {
      message.error("Maximum 20 files allowed");
      return false;
    }

    const existingFilenames = totalFileList.map((f) => f.name);
    if (existingFilenames.includes(file.name)) {
      message.error(`The file "${file.name}" already exists. Please upload a file with a unique name.`);
      return false;
    }

    return true;
  };

  const handleDeletePreviousFile = (index) => {
    const fileId = companyInfo?.files?.[index]?.fileId;
    if (fileId) {
      setDeletedFileIds((prev) => [...prev, fileId]);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("name", companyInfo.name);
    formData.append("website", companyInfo.website);
    formData.append("description", companyInfo.description);
    
    // Add all files from fileList to formData
    fileList.forEach((file) => {
      if (!file.uid.startsWith("existing")) {
        formData.append('files', file.originFileObj || file);
      }
    });

    // Add deleted file IDs to formData
    if (deletedFileIds.length > 0) {
      formData.append('deletedFileIds', JSON.stringify(deletedFileIds));
    }

    try {
      if (!companyInfo.name || !companyInfo.website || !companyInfo.description) {
        throw new Error("Please fill in all required fields");
      }

      if (fileList.length === 0 && !existingData) {
        throw new Error("Please upload at least one file");
      }

      const url = existingData
        ? `/api/company-submission/update/${existingData._id}`
        : "/api/company-submission/submit";

      const response = await axiosSecureInstance.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 10000,
      });

      if (response.status === 201 || response.status === 200) {
        message.success(
          existingData
            ? "Company information updated successfully!"
            : "Company information saved successfully!"
        );

        const updatedData = response.data;
        setExistingData(updatedData);
        setCompanyInfo({
          name: updatedData.companyName || companyInfo.name,
          website: updatedData.websiteUrl || companyInfo.website,
          description: updatedData.companyInformation || companyInfo.description,
          files: updatedData.fileInfo.filter((file)=>file.generatedDoc === false) || [],
        });
        setFileList([]);
        setTotalFileList([]);
        setCountTotalFile(0);
        setDeletedFileIds([]); // Clear deleted file IDs after successful update
        setIsEditing(false);
        fetchCompanyInfo(); // Refresh the company info
      }
    } catch (error) {
      if (error.response) {
        message.error(error.response.data.message || "Failed to save company information");
      } else if (error.request) {
        message.error("Network error: Unable to reach the server");
      } else {
        message.error(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateDoc = async () => {
    if (!companyInfo.website) {
      message.error("Please enter a website URL to generate the document");
      return;
    }

    setIsGeneratingDoc(true);
    try {
      const response = await axiosSecureInstance.post(
        "/api/company-submission/generate-docx",
        { url: companyInfo.website, userId },
        { responseType: "json" }
      );

      const { downloadUrl,fileName,s3Url } = response.data;
      // const fileName = downloadUrl.split('/').pop();
      setDocPreview({ url: downloadUrl, name: fileName });
      message.success("DOCX file generated successfully!");
      const fileDetails = [
        {
          name: fileName,
          size: 0,
          url: downloadUrl,
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          parentId: null,
          isKnowledgeBaseShareable: false,
          isFileShared: false,
          sharedKnowledgeBaseOwner: null,
        },
      ];

      const knowledgeBaseResponse = await createKnowledgeBase({
        fileDetails,
        owner: userId,
      });
      if (knowledgeBaseResponse.success) {
        message.success("DOCX file added to Knowledge Base!");
      } else {
        message.error(`Failed to add file to Knowledge Base: ${knowledgeBaseResponse.message}`);
        console.error("Knowledge Base API failed:", knowledgeBaseResponse.message);
      }
    } catch (error) {
      console.error("Error in handleGenerateDoc:", error);
      if (error.response) {
        message.error(error.response.data.message || "Failed to generate DOCX file");
      } else if (error.request) {
        message.error("Network error: Unable to reach the server");
      } else {
        message.error(error.message);
      }
    } finally {
      setIsGeneratingDoc(false);
    }
  };

  const getFileName = (filePath) => {
    return filePath ? filePath.split("/").pop() : "No file uploaded";
  };

  const handleDeleteFile = async (file) => {
    try {
      if (!existingData?._id) {
        message.error("Company information not found");
        return;
      }

      // Check if it's an existing file (from database) or a newly uploaded file
      if (file.uid.startsWith('existing-')) {
        // For existing files, add to deletedFileIds
        setDeletedFileIds(prev => [...prev, file.fileId]);
      } else {
        // For newly uploaded files, just remove from fileList and totalFileList
        setFileList(prev => prev.filter(f => f.uid !== file.uid));
        setTotalFileList(prev => prev.filter(f => f.uid !== file.uid));
        setCountTotalFile(prev => prev - 1);
      }

      // Update the UI immediately
      setCompanyInfo(prev => ({
        ...prev,
        files: prev.files.filter(f => f.fileId !== file.fileId)
      }));

      message.success("File removed from list. Click Update to save changes.");
    } catch (error) {
      console.error("Error removing file:", error);
      message.error(error.response?.data?.message || "Failed to remove file");
    }
  };

  const handleDeleteGeneratedFile = async () => {
    try {
      const fileId = docPreview.url.split('/').pop();
      const response = await axiosSecureInstance.delete(`/api/company-submission/generated-file/${fileId}`);
      
      if (response.status === 200) {
        message.success("Generated file deleted successfully");
        setDocPreview(null);
        fetchCompanyInfo(); // Refresh the company info
      }
    } catch (error) {
      console.error('Error deleting generated file:', error);
      message.error(error.response?.data?.message || "Failed to delete generated file");
    }
  };

  return (
    <div className="company-info-container" style={{ padding: '24px', borderRadius: '8px' }}>
      <div className="header-section" style={{ marginBottom: '24px' }}>
        <Title level={4} className="component-title">
          <InfoCircleOutlined style={{ marginRight: '8px' }} />
          Add Your Company Information
        </Title>
        <Text className="component-subtitle" type="secondary">
          Provide details about your company to help us serve you better
        </Text>
      </div>

      <Row gutter={[24, 24]}>
        <Col span={12}>
          <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            Company Name
            <Tooltip title="Enter your official company name">
              <InfoCircleOutlined style={{ marginLeft: '8px' }} />
            </Tooltip>
          </label>
          <Input
            name="name"
            value={companyInfo.name}
            onChange={handleChange}
            placeholder="Enter company name"
            disabled={!isEditing}
            style={{ borderRadius: '4px', opacity: 1 }}
          />
        </Col>
        <Col span={12}>
          <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            Website URL
            <Tooltip title="Provide your company's website URL (e.g., https://example.com)">
              <InfoCircleOutlined style={{ marginLeft: '8px' }} />
            </Tooltip>
          </label>
          <Input
            name="website"
            value={companyInfo.website}
            onChange={handleChange}
            placeholder="https://example.com"
            disabled={!isEditing}
            style={{ borderRadius: '4px', opacity: 1 }}
          />
        </Col>
        <Col span={24}>
          <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            Company Description
            <Tooltip title="Describe your company's mission, services, and key information">
              <InfoCircleOutlined style={{ marginLeft: '8px' }} />
            </Tooltip>
          </label>
          <TextArea
            name="description"
            value={companyInfo.description}
            onChange={handleChange}
            rows={4}
            placeholder="Describe your company's mission and services"
            disabled={!isEditing}
            style={{ borderRadius: '4px', opacity: 1 }}
          />
        </Col>
        <Col span={12}>
          <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            Brand Voice
            <Tooltip title="Upload your company information or brand document (PDF, DOCX, or TXT)">
              <InfoCircleOutlined style={{ marginLeft: '8px' }} />
            </Tooltip>
          </label>
          {isEditing ? (
            <Upload
              onChange={handleFileChange}
              beforeUpload={handleAddFile}
              onRemove={handleRemoveFile}
              accept=".pdf,.docx,.txt"
              multiple={true}
              fileList={fileList}
            >
              <Button icon={<UploadOutlined />} style={{ borderRadius: "4px" }}>
                Upload Document
              </Button>
            </Upload>
          ) : (
            <Upload
              fileList={companyInfo.files?.map((file, index) => ({
                uid: file.fileId || `existing-${index}`,
                name: file.fileName,
                status: 'done',
                type: file.fileType
              })) || []}
              showUploadList={{
                showRemoveIcon: false
              }}
              disabled={true}
            >
              <Button icon={<UploadOutlined />} style={{ borderRadius: "4px" }} disabled>
                Upload Document
              </Button>
            </Upload>
          )}
        </Col>
      </Row>

      <div
        className="action-buttons-container" style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-start', gap: '10px' }}
      >
        {isEditing ? (
          <Tooltip title="Save company information">
            <Button
              type="primary"
              loading={isLoading}
              onClick={handleSubmit}
              icon={<SaveOutlined />}
              style={{ borderRadius: '4px', minWidth: '120px' }}
            >
              {existingData ? "Update" : "Save"}
            </Button>
          </Tooltip>
        ) : (
          <>
            <Tooltip title="Edit company information">
              <Button
                type="primary"
                onClick={handleEditClick}
                icon={<EditOutlined />}
                style={{ borderRadius: '4px', minWidth: '120px' }}
              >
                {existingData ? "Edit" : "Add"}
              </Button>
            </Tooltip>
            {existingData && (
              <Tooltip title="Generate document from website">
                <Button
                  type="default"
                  onClick={handleGenerateDoc}
                  loading={isGeneratingDoc}
                  icon={<FileWordOutlined />}
                  style={{ borderRadius: '4px', minWidth: '120px' }}
                >
                  Generate Website Document
                </Button>
              </Tooltip>
            )}
          </>
        )}
      </div>

      {docPreview && (
        <div
          className="doc-file-preview"
          style={{
            marginTop: '24px',
            padding: '16px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <FileWordOutlined style={{ fontSize: '20px', marginRight: '12px' }} />
          <Text strong style={{ marginRight: '12px' }}>Generated FireCrawled Document:</Text>
          <Text code>{docPreview.name.replace(".pdf",".docx")}</Text>
          <Tooltip title="Download generated document">
            <Button
              type="link"
              icon={<DownloadOutlined />}
              style={{ marginLeft: '12px' }}
              onClick={async () => {
                try {
                  const response = await axiosSecureInstance.get(docPreview.url, {
                    responseType: 'blob'
                  });
                  
                  // Create a blob from the response data
                  const blob = new Blob([response.data], { 
                    type: response.headers['content-type'] 
                  });
                  
                  // Create a URL for the blob
                  const url = window.URL.createObjectURL(blob);
                  
                  // Create a temporary link element
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = docPreview.name.replace('.pdf','.docx');
                  
                  // Append to body, click, and remove
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  
                  // Clean up the URL
                  window.URL.revokeObjectURL(url);
                  fetchCompanyInfo(); // Refresh the company info
                } catch (error) {
                  console.error('Error downloading file:', error);
                  message.error('Failed to download file');
                }
              }}
            >
              Download
            </Button>
          </Tooltip>
          <Tooltip title="Delete generated document">
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              style={{ marginLeft: '8px' }}
              onClick={() => {
                Modal.confirm({
                  title: 'Delete Generated Document',
                  content: 'Are you sure you want to delete this generated document? This action cannot be undone.',
                  okText: 'Yes, Delete',
                  okType: 'danger',
                  cancelText: 'No, Cancel',
                  onOk: handleDeleteGeneratedFile
                });
              }}
            >
              Delete
            </Button>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default CompanyInfo;